import { useState } from 'react'
import Image from 'next/image'
import { cn } from '~shared/utils/cn'
import { LeadFormDialog } from '~widgets/lead-form/components/lead-form-dialog'
import { Button } from '~shared/components/button-v2'
import { getProgramData } from '~shared/api/single-program-data/single-program-data'
import image from './images/person-studying.jpg'

// ─── Types ────────────────────────────────────────────────────────────────────

type TExamFreeEnrollmentProps = {
  program: Awaited<ReturnType<typeof getProgramData>>
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TITLE = 'Как поступить без ЕГЭ?'
const TITLE_LEARN_FASTER = 'Учитесь быстрее'
const DESCRIPTION =
  'Оставь заявку и узнай подробности, как получить высшее образование без ЕГЭ по специальной программе'
const BTN = 'Узнать подробнее'

// ─── Slugify ──────────────────────────────────────────────────────────────────

const slugify = (str: string) =>
  str
    .toLowerCase()
    .replace(/[*+~.()'"!:@]/g, '')
    .replace(/\s+/g, '-')
    .trim()

// ─── Component ────────────────────────────────────────────────────────────────

export const ExamFreeEnrollmentSection = ({
  program
}: TExamFreeEnrollmentProps) => {

  const [isOpen, setIsOpen] = useState(false)

  const heading = ['kursy'].includes(program?.category?.slug ?? '')? TITLE_LEARN_FASTER : TITLE

  return (
    <section id={slugify(heading)} className='py-[56px] 2lg:py-[80px]'>
      <div className='mx-auto px-[16px] box-border max-w-[672px] md:max-w-[864px] 2lg:max-w-[1280px]'>
        <div className='relative'>
          {/* Main card */}
          <div
            className={cn(
              'rounded-[16px] bg-[#3a52ee] text-white ',
              'pt-[24px] pb-[24px] px-[24px]',
              'md:pt-[40px] md:pb-[40px] md:px-[40px]',
              '2lg:pt-[56px] 2lg:pb-[56px] 2lg:pl-[56px] 2lg:w-[calc(100%-180px)]',
              '2lg:pr-[210px]'
            )}>
            <h2 className='text-[32px] md:text-[40px] 2lg:text-[48px] font-medium leading-[120%] mb-[16px] 2lg:mb-[24px]'>
              {heading}
            </h2>
            <p className='text-[16px] md:text-[18px] 2lg:text-[20px] leading-[140%] mb-[40px] 2lg:mb-[56px]'>
              {DESCRIPTION}
            </p>

            <Button
              size='slg'
              variant='lead'
              onClick={() => setIsOpen(true)}
              className='w-full md:w-fit md:min-w-[208px] 2lg:min-w-[208px]'>
              {BTN}
            </Button>

            <LeadFormDialog
              open={isOpen}
              onOpenChange={setIsOpen}
              program={{
                productId: program?.productUid ?? null,
                productName: program?.name ?? '',
                productCategory: program?.category?.name ?? '',
                productFaculty: program?.faculty?.name ?? '',
                institute: program?.institute?.name ?? ''
              }}
            />

            {/* Image — mobile/tablet: inside card */}
            <div className='2lg:hidden mt-[40px] rounded-[16px] overflow-hidden'>
              <Image
                src={image}
                alt='Человек учится'
                className='w-full h-auto rounded-[16px]'
                quality={90}
              />
            </div>
          </div>

          {/* Image — desktop: absolute right */}
          <div className='hidden 2lg:block absolute top-1/2 -translate-y-1/2 right-0 max-w-[384px] rounded-[16px] overflow-hidden'>
            <Image
              src={image}
              alt='Человек учится'
              className='min-w-full h-auto rounded-[16px]'
              quality={90}
            />
          </div>
        </div>
      </div>
    </section>
  )
}
